import React from "react";

export type InlineRenameProps = {
  value: string;
  onSubmit: (next: string) => void | Promise<void>;
  onCancel?: () => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  /** Rendered when not editing; clicking / double-clicking enters edit mode. */
  children: (props: { edit: () => void; label: string }) => React.ReactNode;
  /** Minimum length to accept; default 1. */
  minLength?: number;
  /** If provided, value is trimmed before submit. */
  trim?: boolean;
  /** If true, editing starts in open state. */
  initiallyEditing?: boolean;
  /** Disable edit entirely. */
  disabled?: boolean;
};

/**
 * Click-to-edit text primitive with Enter / Esc / blur semantics.
 * The render prop keeps the read-only presentation owned by the caller so
 * tree rows, cards, etc. can each apply their own styles without duplication.
 */
export default function InlineRename({
  value,
  onSubmit,
  onCancel,
  placeholder,
  className = "",
  inputClassName = "",
  children,
  minLength = 1,
  trim = true,
  initiallyEditing = false,
  disabled = false,
}: InlineRenameProps) {
  const [editing, setEditing] = React.useState(initiallyEditing);
  const [draft, setDraft] = React.useState(value);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  React.useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const stop = () => {
    setEditing(false);
    onCancel?.();
  };

  const submit = async () => {
    const next = trim ? draft.trim() : draft;
    if (next.length < minLength) {
      stop();
      return;
    }
    if (next === value) {
      setEditing(false);
      return;
    }
    try {
      await onSubmit(next);
      setEditing(false);
    } catch {
      // Leave editing open on error; caller shows the toast.
    }
  };

  if (disabled || !editing) {
    return (
      <span className={className}>
        {children({
          edit: () => !disabled && setEditing(true),
          label: value,
        })}
      </span>
    );
  }

  return (
    <input
      ref={inputRef}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          submit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setDraft(value);
          stop();
        }
      }}
      onBlur={() => {
        submit();
      }}
      className={`h-8 w-full rounded-md border border-brand-400 bg-white px-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-brand-300 dark:border-brand-300 dark:bg-navy-900 dark:text-white ${inputClassName}`.trim()}
    />
  );
}
